import { PieChart as PieChartIcon } from "lucide-react"
import { Cell, Label, Pie, PieChart } from "recharts"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ChartContainer, ChartTooltip } from "@/components/ui/chart"
import { Empty } from "@/components/ui/empty"
import { formatCurrency } from "@/lib/utils"

// IMP-401: recurring revenue grouped by agent product (Receptionist, Lead
// Responder, …), replacing the former account-industry breakdown. Same donut
// visual and MRR metric; only the grouping dimension changed.
export interface AgentBreakdown {
  data: { product: string; count: number; mrr_cents: number }[]
  total_mrr_cents: number
}

const PALETTE = [
  "var(--color-pink-400)",
  "var(--color-purple-500)",
  "var(--color-sky-400)",
  "var(--color-blue-500)",
  "var(--color-indigo-500)",
]
const OTHER_COLOR = "var(--color-zinc-400)"

function paletteColor(i: number, product?: string) {
  return product === "Other" ? OTHER_COLOR : PALETTE[i % PALETTE.length]
}

function fmtShort(n: number) {
  if (n >= 1_000_000)
    return "$" + (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M"
  if (n >= 1_000) return "$" + (n / 1_000).toFixed(1).replace(/\.0$/, "") + "k"
  return "$" + n
}

export function AgentChart({ breakdown }: { breakdown: AgentBreakdown }) {
  const data = breakdown.data.filter((d) => d.count > 0 && d.mrr_cents > 0)
  const total_mrr_cents = data.reduce((sum, d) => sum + d.mrr_cents, 0)

  if (data.length === 0) {
    return (
      <Card className="flex h-full flex-col rounded-sm">
        <CardHeader>
          <CardTitle className="text-base font-semibold">
            MRR by Agent
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-1 items-center justify-center">
          <Empty
            icon={<PieChartIcon className="h-5 w-5" />}
            title="No revenue to display"
          />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="flex h-full flex-col rounded-sm">
      <CardHeader>
        <CardTitle className="text-base font-semibold">MRR by Agent</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        <ChartContainer config={{}} className="h-[300px] w-full">
          <PieChart>
            <ChartTooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null
                const { name, value } = payload[0]
                const idx = data.findIndex((d) => d.product === name)
                const color = paletteColor(idx, name as string)
                return (
                  <div className="border-border/50 bg-background rounded-lg border px-2.5 py-1.5 text-xs shadow-md">
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-muted-foreground">
                        {name as string}
                      </span>
                      <span className="text-foreground font-mono font-medium tabular-nums">
                        {formatCurrency((value as number) / 100)}
                      </span>
                    </div>
                  </div>
                )
              }}
            />
            {/* Outer ring */}
            <Pie
              data={data}
              dataKey="mrr_cents"
              nameKey="product"
              innerRadius={109}
              outerRadius={116}
              paddingAngle={4}
              strokeWidth={0}
              isAnimationActive={false}
            >
              {data.map((d, i) => (
                <Cell key={d.product} fill={paletteColor(i, d.product)} />
              ))}
            </Pie>
            {/* Inner fill ring */}
            <Pie
              data={data}
              dataKey="mrr_cents"
              nameKey="product"
              innerRadius={86}
              outerRadius={108}
              paddingAngle={4}
              strokeWidth={0}
              isAnimationActive={false}
            >
              {data.map((d, i) => (
                <Cell
                  key={d.product}
                  fill={paletteColor(i, d.product)}
                  fillOpacity={0.15}
                />
              ))}
              <Label
                content={({ viewBox }) => {
                  if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy ?? 0) - 14}
                          fontSize={14}
                          fontWeight={500}
                          fill="var(--color-zinc-500)"
                        >
                          MRR
                        </tspan>
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy ?? 0) + 16}
                          fontSize={30}
                          fontWeight={400}
                          letterSpacing="-0.025em"
                          fill="currentColor"
                        >
                          {fmtShort(total_mrr_cents / 100)}
                        </tspan>
                      </text>
                    )
                  }
                }}
              />
            </Pie>
          </PieChart>
        </ChartContainer>
        <div className="min-h-4 flex-1" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          {data.map((d, i) => (
            <div
              key={d.product}
              className="flex items-center justify-between text-sm"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: paletteColor(i, d.product) }}
                />
                <span className="text-muted-foreground truncate">
                  {d.product}
                </span>
              </div>
              <span className="text-foreground ml-2 shrink-0 tabular-nums">
                {formatCurrency(d.mrr_cents / 100)}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
